<template>
  <!-- Shown when the API reports the storefront is switched off (503
       store_unavailable) or that nothing resolves to this host (404).

       The two get different words. "Closed" is a shopper's answer to a seller's
       own switch; a host that reaches us but maps to no store is not closed, it
       is not connected, and saying "closed" sent the one person who could fix it
       to the one setting that was not broken.

       Every style here is INLINE-scoped and uses literal values, not the
       --dk-* theme tokens: theme.css is served by the same tenant-resolving
       endpoint, so on a switched-off store it 503s too and no token exists.
       A screen that depends on the theme would render as unstyled black text
       on white exactly when it is the only thing a visitor sees. -->
  <div class="dk-unavail">
    <div class="dk-unavail__box">
      <svg class="dk-unavail__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
        <path d="M3 9h18M5 9V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v3M5 9v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M9 14h6" stroke-linecap="round" />
      </svg>
      <h1 class="dk-unavail__title">{{ title }}</h1>
      <p class="dk-unavail__text">{{ text }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"

const props = defineProps<{ reason: "closed" | "unconnected" }>()

const unconnected = computed(() => props.reason === "unconnected")
const title = computed(() =>
  unconnected.value ? "This domain isn't connected to a store" : "This store is not available",
)
// The second line is read by whoever pointed the DNS here far more often than by
// a shopper, so it names the one remaining step rather than apologising.
const text = computed(() =>
  unconnected.value
    ? "The domain reaches us, but it isn't linked to a store yet. If it's yours, finish connecting it in your dashboard under Settings → Domains."
    : "The shop is currently closed. Please check back later.",
)
</script>

<style>
.dk-unavail {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f7f7f7;
  color: #1a1a1a;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}
.dk-unavail__box {
  max-width: 26rem;
  text-align: center;
}
.dk-unavail__icon {
  width: 40px;
  height: 40px;
  margin: 0 auto 16px;
  color: #8a8a8a;
}
.dk-unavail__title {
  margin: 0 0 8px;
  font-size: 1.375rem;
  font-weight: 600;
  line-height: 1.3;
}
.dk-unavail__text {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: #6b6b6b;
}
@media (prefers-color-scheme: dark) {
  .dk-unavail {
    background: #141414;
    color: #f2f2f2;
  }
  .dk-unavail__text {
    color: #a3a3a3;
  }
}
</style>
